import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { useCourseDtoActions, useModalConfirm } from "@ryde/redux";
import {
  ConfirmResult,
  courseHasUnsavedStationChangeAtom,
  CourseMetadataKeys,
  CourseRouteTypeEnum,
  CoursesApi,
  createRouteRebuildAppliedAt,
  DirectionsServiceEnum,
  errors,
  formatRouteRebuildSuccessToast,
  ICourseDto,
  ICourseMetadata,
  IRebuildRouteDefaultActionOptions,
  isCustomRebuildRouteType,
  isDefaultActionRebuildRouteType,
  lastRouteRebuildAtAtom,
  lastSelectedRebuildTypeAtom,
  Log,
  normalizeCourseMetadata,
  PROVIDERS_WITH_AVOID_TOLLS,
  rebuildRouteLoadingAtom,
  RebuildRouteTypeEnum,
  RecountCourseStationsCriteriaDto,
  resolveInitialRebuildRouteProvider,
  resolveRebuildRouteProvider,
  resolveRecountedStationsInfo,
  resolveSkipCacheDefaultRebuildType,
  routeHasUserOwnedRoutingWaypoints,
  SimpleResult,
  toDisplayedRebuildRouteType,
  useSystemParams,
  useTimezone,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const useCourseRebuildRouteDropdown = (
  courseDto: Partial<ICourseDto> | undefined,
  isManualPolylineEditFromForm?: boolean,
  syncFormPolylineAfterRebuild?: (routePolyline: string | undefined) => void,
) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { updateEditingCourseMetadata } = useCourseDtoActions();
  const { confirmDialogResult } = useModalConfirm();
  const systemParams = useSystemParams();

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useRecoilState(rebuildRouteLoadingAtom);
  const lastSelectedRebuildType = useRecoilValue(lastSelectedRebuildTypeAtom);
  const lastRouteRebuildAt = useRecoilValue(lastRouteRebuildAtAtom);
  const setLastSelectedRebuildType = useSetRecoilState(
    lastSelectedRebuildTypeAtom,
  );
  const setLastRouteRebuildAt = useSetRecoilState(lastRouteRebuildAtAtom);
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );

  const systemDefaultProvider =
    systemParams.directionsService || DirectionsServiceEnum.Google;
  const lastUsedProvider = courseDto?.metadata?.directionsService;

  const [selectedProvider, setSelectedProvider] =
    useState<DirectionsServiceEnum | null>(() =>
      resolveInitialRebuildRouteProvider({
        lastUsedProvider,
        systemDefaultProvider,
      }),
    );
  const [avoidTolls, setAvoidTolls] = useState<boolean>(
    systemParams.avoidTolls ?? false,
  );
  const [skipCache, setSkipCache] = useState(true);
  const hasUserOwnedRoutingWaypoints = useMemo(
    () => routeHasUserOwnedRoutingWaypoints(courseDto?.metadata?.stationsInfo),
    [courseDto?.metadata?.stationsInfo],
  );
  const isShuttleLikeCourse =
    courseDto?.routeType === CourseRouteTypeEnum.Shuttle ||
    courseDto?.routeType === CourseRouteTypeEnum.Flex;
  const getDefaultRouteOption = (): RebuildRouteTypeEnum =>
    RebuildRouteTypeEnum.KeepOrder;
  const [routeOptionTouched, setRouteOptionTouched] = useState(false);
  const [providerTouched, setProviderTouched] = useState(false);
  const [routeOption, setRouteOptionState] = useState<RebuildRouteTypeEnum>(
    () => getDefaultRouteOption(),
  );
  const setRouteOption = (option: RebuildRouteTypeEnum): void => {
    setRouteOptionTouched(true);
    setRouteOptionState(option);
  };
  const setSelectedProviderWithTouch = (
    provider: DirectionsServiceEnum,
  ): void => {
    setProviderTouched(true);
    setSelectedProvider(provider);
  };

  useEffect(() => {
    setProviderTouched(false);
  }, [courseDto?.id]);

  useEffect(() => {
    if (hasUserOwnedRoutingWaypoints) {
      setSkipCache(true);
    }
  }, [hasUserOwnedRoutingWaypoints]);

  useEffect(() => {
    if (providerTouched) {
      return;
    }
    setSelectedProvider(
      resolveInitialRebuildRouteProvider({
        lastUsedProvider,
        systemDefaultProvider,
      }),
    );
  }, [lastUsedProvider, systemDefaultProvider, providerTouched, courseDto?.id]);

  useEffect(() => {
    const isReorderStationsOption =
      routeOption === RebuildRouteTypeEnum.ProviderOptimization ||
      routeOption === RebuildRouteTypeEnum.OptimizeStationOrderOsrmFirst;
    const shouldForceKeepOrder =
      !routeOptionTouched || (isShuttleLikeCourse && isReorderStationsOption);

    if (
      shouldForceKeepOrder &&
      routeOption !== RebuildRouteTypeEnum.KeepOrder
    ) {
      setRouteOptionState(RebuildRouteTypeEnum.KeepOrder);
    }
  }, [isShuttleLikeCourse, routeOption, routeOptionTouched]);

  const confirmRemoveManualPolylineIfNeeded = async (): Promise<boolean> => {
    const isManualPolylineEditEffective =
      isManualPolylineEditFromForm === true ||
      courseDto?.metadata?.[CourseMetadataKeys.isManualPolylineEdit] === true;
    if (!isManualPolylineEditEffective) {
      return true;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("course.REBUILD_WILL_REMOVE_MANUAL_POLYLINE_CONFIRM"),
    });

    return confirmationResult === ConfirmResult.OK;
  };

  const handleDropdownToggle = useEvent((event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(prevOpen => !prevOpen);
  });

  const handleClose = useEvent((_event: Event | React.SyntheticEvent) => {
    setOpen(false);
  });

  const supportsAvoidTolls = selectedProvider
    ? PROVIDERS_WITH_AVOID_TOLLS.includes(selectedProvider)
    : true;

  const resolveDefaultRebuildType = (): RebuildRouteTypeEnum =>
    resolveSkipCacheDefaultRebuildType({
      skipCachedPaths: skipCache,
      preferSkipCacheWithoutUpdate: hasUserOwnedRoutingWaypoints,
    });

  const handleDefaultRebuild = useEvent(
    async (
      rebuildType?: RebuildRouteTypeEnum,
      options?: IRebuildRouteDefaultActionOptions,
    ) => {
      const stations = courseDto?.metadata?.stationsInfo || [];

      if (!stations?.length) {
        Log.warnAndToast(t(errors.course.noStationToRebuild));
        return;
      }

      if (isLoading) {
        return;
      }

      if (!(await confirmRemoveManualPolylineIfNeeded())) {
        return;
      }

      const resolvedRebuildType = isDefaultActionRebuildRouteType(rebuildType)
        ? rebuildType
        : resolveDefaultRebuildType();

      setIsLoading(true);
      setOpen(false);

      try {
        const provider =
          options?.provider ??
          resolveInitialRebuildRouteProvider({
            lastUsedProvider,
            systemDefaultProvider,
          });

        const criteriaRecount = new RecountCourseStationsCriteriaDto({
          stations,
          optimize: true,
          provider,
          rebuildType: resolvedRebuildType,
        });

        const recounted = await CoursesApi.recountStationInfo(criteriaRecount);

        if (!recounted?.stationsInfo) {
          warnAndToastSimpleResult(
            new SimpleResult({
              success: false,
              error: errors.course.stationsNotRecountedSuccessfully,
            }),
            t,
          );
          return;
        }

        syncFormPolylineAfterRebuild?.(recounted.routePolyline);

        const metadataCloned: ICourseMetadata = {
          ...(courseDto?.metadata || {}),
        };
        if (metadataCloned[CourseMetadataKeys.isManualPolylineEdit]) {
          metadataCloned[CourseMetadataKeys.isManualPolylineEdit] = false;
        }
        metadataCloned.stationsInfo = resolveRecountedStationsInfo({
          recounted: recounted.stationsInfo ?? [],
          source: stations,
          sourceRouteIds: {},
          recountRouteIds: {},
        });
        // Always overwrite routePolyline from recount (can be empty/undefined)
        metadataCloned[CourseMetadataKeys.routePolyline] =
          recounted.routePolyline;
        metadataCloned.totalDuration = recounted.totalDuration;
        metadataCloned.totalDistance = recounted.totalDistance;
        if (recounted.directionsService) {
          metadataCloned.directionsService = recounted.directionsService;
        }
        const routeRebuildAt = createRouteRebuildAppliedAt();
        setLastRouteRebuildAt(routeRebuildAt);

        const metadataRecounted = normalizeCourseMetadata({
          metadata: metadataCloned,
          reorderStationsByTime: true,
          timezone,
        });

        const result = await updateEditingCourseMetadata({
          metadata: metadataRecounted,
          optimize: true,
          skipApiCheck: true,
        });

        if (result.success) {
          // Recounted metadata is applied locally only — mark unsaved so Save stays enabled.
          setCourseHasUnsavedStationChange(true);
          setLastSelectedRebuildType(
            toDisplayedRebuildRouteType(resolvedRebuildType),
          );
          Log.infoAndToast(
            formatRouteRebuildSuccessToast({
              baseMessage: t("course.ROUTE_REBUILD_SUCCESSFULLY"),
              provider: recounted.directionsService ?? provider,
              routeRebuildSource: recounted.routeRebuildSource,
              t,
            }),
          );
        } else {
          warnAndToastSimpleResult(result, t);
        }
      } finally {
        setIsLoading(false);
      }
    },
  );

  const handleRebuild = useEvent(async () => {
    const stations = courseDto?.metadata?.stationsInfo || [];

    if (!stations?.length) {
      Log.warnAndToast(t(errors.course.noStationToRebuild));
      return;
    }

    if (isLoading) {
      return;
    }

    if (!(await confirmRemoveManualPolylineIfNeeded())) {
      return;
    }

    const rebuildType =
      routeOption || RebuildRouteTypeEnum.ProviderOptimization;

    setIsLoading(true);

    try {
      const provider = resolveRebuildRouteProvider({
        selectedProvider,
        lastUsedProvider,
        systemDefaultProvider,
      });

      // For courses: ProviderOptimization and OptimizeStationOrderOsrmFirst always use optimize=true
      // (no exceptions, unlike rides)
      const optimize =
        rebuildType === RebuildRouteTypeEnum.ProviderOptimization ||
        rebuildType === RebuildRouteTypeEnum.OptimizeStationOrderOsrmFirst ||
        rebuildType === RebuildRouteTypeEnum.EndpointsLocked;

      const criteriaRecount = new RecountCourseStationsCriteriaDto({
        stations,
        optimize,
        avoidTolls,
        provider,
        rebuildType,
      });

      const recounted = await CoursesApi.recountStationInfo(criteriaRecount);

      if (!recounted?.stationsInfo) {
        warnAndToastSimpleResult(
          new SimpleResult({
            success: false,
            error: errors.course.stationsNotRecountedSuccessfully,
          }),
          t,
        );
        return;
      }

      syncFormPolylineAfterRebuild?.(recounted.routePolyline);

      const metadataCloned: ICourseMetadata = {
        ...(courseDto?.metadata || {}),
      };
      if (metadataCloned[CourseMetadataKeys.isManualPolylineEdit]) {
        metadataCloned[CourseMetadataKeys.isManualPolylineEdit] = false;
      }
      metadataCloned.stationsInfo = resolveRecountedStationsInfo({
        recounted: recounted.stationsInfo ?? [],
        source: stations,
        sourceRouteIds: {},
        recountRouteIds: {},
      });
      // Always overwrite routePolyline from recount (can be empty/undefined)
      metadataCloned[CourseMetadataKeys.routePolyline] =
        recounted.routePolyline;
      metadataCloned.totalDuration = recounted.totalDuration;
      metadataCloned.totalDistance = recounted.totalDistance;
      if (recounted.directionsService) {
        metadataCloned.directionsService = recounted.directionsService;
      }
      const routeRebuildAt = createRouteRebuildAppliedAt();
      setLastRouteRebuildAt(routeRebuildAt);

      const metadataRecounted = normalizeCourseMetadata({
        metadata: metadataCloned,
        reorderStationsByTime: true,
        timezone,
      });

      const result = await updateEditingCourseMetadata({
        metadata: metadataRecounted,
        optimize,
        // We already called `CoursesApi.recountStationInfo` above (with provider/avoidTolls/rebuildType).
        // Setting `skipApiCheck` prevents a second recount call inside `updateEditingCourseMetadata`,
        // which would be redundant and could overwrite the just-recounted result.
        skipApiCheck: true,
      });

      if (result.success) {
        // Recounted metadata is applied locally only — mark unsaved so Save stays enabled.
        setCourseHasUnsavedStationChange(true);
        setLastSelectedRebuildType(toDisplayedRebuildRouteType(rebuildType));

        Log.infoAndToast(
          formatRouteRebuildSuccessToast({
            baseMessage: t("course.ROUTE_REBUILD_SUCCESSFULLY"),
            provider: recounted.directionsService ?? provider,
            routeRebuildSource: recounted.routeRebuildSource,
            t,
          }),
        );
      } else {
        warnAndToastSimpleResult(result, t);
      }
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  });

  const hasCustomRebuildType = isCustomRebuildRouteType(
    lastSelectedRebuildType,
  );

  return {
    open,
    isLoading,
    systemDefaultProvider,
    lastUsedProvider,
    selectedProvider,
    setSelectedProvider: setSelectedProviderWithTouch,
    avoidTolls,
    setAvoidTolls,
    skipCache,
    setSkipCache,
    hasUserOwnedRoutingWaypoints,
    routeOption,
    setRouteOption,
    supportsAvoidTolls,
    systemDefaultAvoidTolls: systemParams.avoidTolls ?? false,
    hasCustomRebuildType,
    handleDropdownToggle,
    handleClose,
    handleDefaultRebuild,
    handleRebuild,
    lastSelectedRebuildType,
    lastRouteRebuildAt,
  };
};
